import apiClient from 'helpers/apiClient';
import { isEmpty } from 'lodash';
import { ALL_AUTHOR_GROUP_TARGETS } from './AuthorGroupsUtils';

const serialize = (authorGroup) => {
  if (isEmpty(authorGroup) || isEmpty(authorGroup.name) || isEmpty(authorGroup.members)) {
    throw new Error(`Invalid authorGroup: ${authorGroup}`);
  }
  const memberValues = Object.values(authorGroup.members);
  const targets = memberValues.reduce((obj, memberValue) => {
    if (isEmpty(memberValue.value) || isEmpty(memberValue.authors)) {
      throw new Error(`Invalid targetValue: ${JSON.stringify(memberValue)}`);
    }
    obj[memberValue.value] = [...memberValue.authors];
    return obj;
  }, {});
  return {
    _id: authorGroup.id,
    name: authorGroup.name,
    targets
  };
};

const deserialize = (authorGroupData) => {
  if (isEmpty(authorGroupData)) {
    throw new Error(`Invalid authorGroupData: ${authorGroupData}`);
  }
  const targetKeys = Object.keys(authorGroupData.targets);
  const members = targetKeys.reduce((obj, targetKey, index) => {
    const target = ALL_AUTHOR_GROUP_TARGETS.find(t => t.value === targetKey);
    if (!target) {
      throw new Error(`Invalid target key: ${targetKey}`);
    }
    obj[String(index)] = {...target, authors: [...authorGroupData.targets[targetKey]]};
    return obj;
  }, {});

  return {
    id: authorGroupData._id,
    name: authorGroupData.name,
    members
  };
};

export const getAuthorGroupsFromDB = async (campaignId) => {
  try {
    const response = await apiClient(
      `/api/campaigns/${campaignId}/author_groups`,
      { method: 'GET' }
    );
    if (!Array.isArray(response)) {
      throw new Error(`Unexpected response: ${JSON.stringify(response)}`);
    }
    return response.map(r => deserialize(r));
  } catch (error) {
    throw new Error(`Error fetching all author groups ${JSON.stringify({campaignId, error})}`);
  }
};

export const createAuthorGroupInDB = async (campaignId, authorGroup) => {
  try {
    const authorGroupData = serialize(authorGroup);
    delete authorGroupData._id;
    const response = await apiClient(
      `/api/campaigns/${campaignId}/author_groups`,
      {
        method: 'POST',
        body: JSON.stringify(authorGroupData),
      }
    );
    return deserialize(response);
  } catch (error) {
    throw new Error(`Error fetching creating author group ${JSON.stringify({campaignId, authorGroup, error})}`);
  }
};

export const getAuthorGroupFromDB = async (campaignId, authorGroupId) => {
  try {
    const response = await apiClient(
      `/api/campaigns/${campaignId}/author_groups/${authorGroupId}`,
      { method: 'GET' }
    );
    return deserialize(response);
  } catch (error) {
    throw new Error(`Error fetching author group ${JSON.stringify({campaignId, authorGroupId, error})}`);
  }
};

export const updateAuthorGroupInDB = async (campaignId, updatedAuthorGroup) => {
  try {
    const updatedAuthorGroupData = serialize(updatedAuthorGroup);
    const response = await apiClient(
      `/api/campaigns/${campaignId}/author_groups/${updatedAuthorGroup.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(updatedAuthorGroupData),
      }
    );
    return deserialize(response);
  } catch (error) {
    throw new Error(`Error updating author group ${JSON.stringify({campaignId, updatedAuthorGroup, error})}`);
  }
};

export const deleteAuthorGroupFromDB = async (campaignId, authorGroupId) => {
  try {
    await apiClient(
      `/api/campaigns/${campaignId}/author_groups/${authorGroupId}`,
      { method: 'DELETE' }
    );
  } catch (error) {
    throw new Error(`Error deleting author group ${JSON.stringify({campaignId, authorGroupId, error})}`);
  }
};
